import { Typography } from '@mui/material'
import {
  StoppageReasonResponseDto,
  StoppageReasonResponsible,
  StoppageReasonType,
} from '@operations/app/api'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { PATHS, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'

import { useMemo } from 'react'
import { useNavigate } from 'react-router'

interface Props {
  filteredItems: StoppageReasonResponseDto[]
  toggleDelete: (id?: number) => void
}

export const StoppageReasonsTable = observer(({ filteredItems, toggleDelete }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  // TODO - use id from url
  const handleEdit = (id: number) => {
    navigate(`${PATHS.masterData.stoppageReason.url}/${id}`)
  }

  const columns = useMemo<MRT_ColumnDef<StoppageReasonResponseDto>[]>(
    () => [
      {
        accessorKey: 'shortName',
        header: t('shortName', 'Short name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'description',
        header: t('description', 'Description'),
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        accessorKey: 'responsible',
        header: t('responsible', 'Responsible'),
        Cell: cell => (
          <Typography variant='body2'>
            {t(cell.row.original.responsible, cell.row.original.responsible)}
          </Typography>
        ),
        filterVariant: 'select',
        filterSelectOptions: Object.values(StoppageReasonResponsible).map(value => ({
          label: t(value, value),
          value,
        })),
        filterFn: 'equals',
        enableColumnFilterModes: false,
      },
      {
        accessorKey: 'type',
        header: t('type', 'Type'),
        Cell: cell => (
          <Typography variant='body2'>
            {t(cell.row.original.type, cell.row.original.type)}
          </Typography>
        ),
        filterVariant: 'select',
        filterSelectOptions: Object.values(StoppageReasonType).map(value => ({
          label: t(value, value),
          value,
        })),
        filterFn: 'equals',
        enableColumnFilterModes: false,
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'operations/stoppage-reasons',
  )

  const table = useTomTable<StoppageReasonResponseDto>(
    {
      columns,
      data: filteredItems,
      enableTopToolbar: false,
      enableRowSelection: false,
      enableExpanding: false,
      enableSelectAll: false,
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('editStoppageReason', 'Edit stoppage reason'),
            onClick: () => handleEdit(row.original.id),
            testId: 'edit-stoppage-btn',
          },
          {
            label: t('deleteStoppageReason', 'Delete stoppage reason'),
            onClick: () => toggleDelete(row.original.id),
            testId: 'delete-stoppage-reason-btn',
          },
        ]

        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='stoppage-reason-options-btn'
          />
        )
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return <TomTable table={table} />
})
